import { oc } from "@orpc/contract";
import { z } from "zod";
import { listInput, listPage } from "./list";
import { Cents } from "./money";

/**
 * Discount codes (Kortingscodes, CONTEXT.md `discountCode`): percentage, fixed amount or free
 * shipping, with a validity window, a minimum order amount, limits in total and per customer, and
 * optionally limited to products and/or customer groups. One code per order. Amounts are on the
 * customer group's VAT basis, like every price that group sees. Uses are counted at checkout.
 *
 * Codes are case-insensitive: stored and shown in upper case, unique in any case.
 */

/** The most an amount may be: €10.000. A typo guard, not a business rule. */
const MAX_AMOUNT = 1_000_000;

export const DiscountCodeKind = z.discriminatedUnion("type", [
	z.object({ type: z.literal("percentage"), percent: z.number().int().min(1).max(100) }),
	z.object({ type: z.literal("fixedAmount"), amount: Cents.min(1).max(MAX_AMOUNT) }),
	z.object({ type: z.literal("freeShipping") }),
]);
export type DiscountCodeKind = z.infer<typeof DiscountCodeKind>;
export type DiscountCodeKindType = DiscountCodeKind["type"];

/**
 * Derived from the window, the uses and now, in this order: `expired` (past its end), `usedUp`
 * (reached its total limit), `scheduled` (not started yet), otherwise `active`.
 */
export const DiscountCodeStatus = z.enum(["active", "scheduled", "expired", "usedUp"]);
export type DiscountCodeStatus = z.infer<typeof DiscountCodeStatus>;

/** Who made the code: `manual` in the admin, `welcome` for a newsletter signup's welcome code (#108). */
export const DiscountCodeOrigin = z.enum(["manual", "welcome"]);
export type DiscountCodeOrigin = z.infer<typeof DiscountCodeOrigin>;

const Named = z.object({ id: z.string(), name: z.string() });

export const DiscountCode = z.object({
	id: z.string(),
	code: z.string(),
	kind: DiscountCodeKind,
	/** Valid from `startsAt`, no longer at `endsAt`; `null` is open on that side. */
	startsAt: z.iso.datetime().nullable(),
	endsAt: z.iso.datetime().nullable(),
	/** The goods total before discount must reach this. */
	minimumOrderAmount: Cents.nullable(),
	maxUses: z.number().int().nullable(),
	maxUsesPerCustomer: z.number().int().nullable(),
	timesUsed: z.number().int(),
	/** The products it is limited to, by name; empty: every product. */
	products: z.array(Named),
	/** The customer groups it is limited to, Consument first; empty: every group. */
	customerGroups: z.array(Named),
	status: DiscountCodeStatus,
	origin: DiscountCodeOrigin,
	/** Goes up by one on every save; an update sends the version it was loaded at. */
	version: z.number().int(),
	createdAt: z.iso.datetime(),
	updatedAt: z.iso.datetime(),
});
export type DiscountCode = z.infer<typeof DiscountCode>;

export const DiscountCodeListInput = listInput({
	sort: ["code", "createdAt", "endsAt"],
	defaultSort: "createdAt",
	defaultOrder: "desc",
	filters: {
		status: DiscountCodeStatus.optional().catch(undefined),
		origin: DiscountCodeOrigin.optional().catch(undefined),
	},
});
export type DiscountCodeListInput = z.input<typeof DiscountCodeListInput>;
export type DiscountCodeListQuery = z.output<typeof DiscountCodeListInput>;

export const DiscountCodeList = listPage(DiscountCode);
export type DiscountCodeList = z.infer<typeof DiscountCodeList>;

/** What a customer types: letters, digits, hyphens and underscores. Stored in upper case. */
export const DiscountCodeText = z
	.string()
	.trim()
	.min(3)
	.max(40)
	.regex(/^[A-Za-z0-9_-]+$/, "letters, digits, hyphens and underscores")
	.transform((code) => code.toUpperCase());

const Ids = z
	.array(z.string().min(1))
	.max(200)
	.transform((ids) => [...new Set(ids)]);

/** What the admin's form sends to create a code. */
export const DiscountCodeFields = z
	.object({
		code: DiscountCodeText,
		kind: DiscountCodeKind,
		startsAt: z.iso.datetime({ offset: true }).nullable(),
		endsAt: z.iso.datetime({ offset: true }).nullable(),
		minimumOrderAmount: Cents.min(0).max(MAX_AMOUNT).nullable(),
		maxUses: z.number().int().min(1).max(1_000_000).nullable(),
		maxUsesPerCustomer: z.number().int().min(1).max(1_000_000).nullable(),
		productIds: Ids,
		customerGroupIds: Ids,
	})
	.refine(
		(fields) =>
			fields.startsAt === null ||
			fields.endsAt === null ||
			Date.parse(fields.startsAt) < Date.parse(fields.endsAt),
		{ message: "The end comes after the start", path: ["endsAt"], params: { i18n: "endsBeforeStart" } },
	);
export type DiscountCodeFields = z.output<typeof DiscountCodeFields>;

/** Every field, plus the `version` it loaded: a code saved by someone else meanwhile is refused (409). */
export const DiscountCodeUpdate = DiscountCodeFields.safeExtend({ version: z.number().int().positive() });
export type DiscountCodeUpdate = z.output<typeof DiscountCodeUpdate>;

const ById = z.object({ id: z.string() });

/**
 * Admin only. Errors (`{ _tag, ... }`): 404 `DiscountCodeNotFound`; 409 `DiscountCodeTaken` (the
 * code exists, in any case), `DiscountCodeChanged` (saved by someone else), `DiscountCodeUsed`
 * (deleting a code that was used: end it instead), `DiscountCodeMaxUsesBelowUsed` (a total limit
 * below the uses so far); 422 `DiscountCodeScopeNotFound` (a product or group that does not exist).
 */
export const DiscountCodeContract = {
	list: oc
		.route({ method: "GET", path: "/admin/discount-codes" })
		.input(DiscountCodeListInput)
		.output(DiscountCodeList),
	create: oc
		.route({ method: "POST", path: "/admin/discount-codes", successStatus: 201 })
		.input(DiscountCodeFields)
		.output(DiscountCode),
	get: oc.route({ method: "GET", path: "/admin/discount-codes/{id}" }).input(ById).output(DiscountCode),
	update: oc
		.route({ method: "PUT", path: "/admin/discount-codes/{id}" })
		.input(DiscountCodeUpdate.safeExtend(ById.shape))
		.output(DiscountCode),
	delete: oc.route({ method: "DELETE", path: "/admin/discount-codes/{id}", successStatus: 204 }).input(ById),
};
